import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
	fetchClientList,
	setClientLifecycleStatus,
	deactivateClient,
	activateClient,
} from "../actions/clientActions";
import { toggleShowTrashClients } from "../actions/browserLocalstorageActions";
import {
	getClientLifecycleStatusObject,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";

import CustomizableTable from "./Tables/CustomizableTable";
import { MAIN_COLUMN_WIDTH, ID_COLUMN_WIDTH } from "./Tables/SortableTable";
import InteractiveTableCell, {
	ActionsTableCell,
} from "./Tables/InteractiveTableCell";
import { ButtonWithRouter } from "./ButtonWithIcon";
import { DropDownButtonWithRouter } from "./DropDownButton";
import { IconArrowUp, IconUsersGroup } from "@tabler/icons-react";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";

const tableUniqueID = "/clients";

class ClientList extends Component {
	constructor(props, context) {
		super(props, context);
		this.columnConfig = [
			{ Header: "ID", accessor: "clientID", width: ID_COLUMN_WIDTH },
			{
				Header: "Client Name",
				accessor: "clientName",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) => (
					<InteractiveTableCell
						menuItems={this.getClientMenuItems(row)}
					>
						<Link to={`/clients/${row.original.clientID}/`}>
							{row.value}
						</Link>
					</InteractiveTableCell>
				),
				wildcard: true,
			},
			{
				Header: "Status",
				accessor: "clientStatus",
				Cell: (row) => (
					<span style={{ textTransform: "capitalize" }}>
						{row.value}
					</span>
				),
			},
			{
				Header: "Tags",
				accessor: "tags",
				Cell: (row) =>
					row.value
						? row.value.map((x) => (
								<div className="tagPill" key={x.tagName}>
									{x.tagName}
								</div>
							))
						: "",

				filterValueMethod: (x) =>
					(x.tags || []).map((t) => t.tagName).join(", ") || null,
			},
			{
				// the label was "Primary Attorney(s)" but changed to "Assigned Staff" per UX feedback
				// the database still stores this information in the PrimaryAttorneysForClient table
				Header: "Assigned Staff",
				accessor: "primaryAttorneys",
				Cell: (row) =>
					row.value
						? row.value.map((x) => x.displayName).join(", ")
						: "",
				filterValueMethod: (x) =>
					(x.primaryAttorneys || [])
						.map((s) => s.displayName)
						.join(", ") || null,
			},
			{
				Header: "",
				accessor: "actions",
				width: 50,
				sortable: false,
				filterable: false,
				Cell: (row) => (
					<ActionsTableCell
						row={row}
						itemLifecycleStatusObject={getClientLifecycleStatusObject(
							row.original.clientLifecycleStatus
						)}
						matterOrFolderLifecycleStatusObject={{
							disabled: false,
						}}
						trashClickHandler={() => {
							this.props.setClientLifecycleStatus({
								clientIDs: [row.original.clientID],
								lifecycleStatus:
									row.original.clientLifecycleStatus ===
									TRASH_LIFECYCLE_STATUS
										? NORMAL_LIFECYCLE_STATUS
										: TRASH_LIFECYCLE_STATUS,
								location: "clientList",
							});
						}}
					/>
				),
			},
		];
		this.filterMap = {
			All: () => true,
			Active: (row) => row.clientStatus === "active",
			Inactive: (row) => row.clientStatus === "inactive",
			My: (row) => {
				return (
					row.primaryAttorneys &&
					row.primaryAttorneys.length &&
					row.primaryAttorneys.reduce(
						(acc, cur) =>
							acc || cur.cognitoSub === this.props.cognitoSub,
						false
					)
				);
			},
		};
		this.getClientMenuItems = this.getClientMenuItems.bind(this);
	}

	componentDidMount() {
		this.props.fetchClientList();
	}

	getClientMenuItems = (row) => {
		let menuItems = [
			{
				text: "Edit Client",
				href: `/clients/${row.original.clientID}/edit`,
			},
		];

		if (row.original.clientStatus === "active") {
			menuItems.push({
				text: "Deactivate Client",
				disabled:
					row.original.clientLifecycleStatus ===
					TRASH_LIFECYCLE_STATUS,
				disabledHoverText:
					"Cannot deactivate a client in the Trash Bin",
				onSelect: () =>
					this.props.deactivateClient({
						clientID: row.original.clientID,
						location: "clientList",
					}),
			});
		}
		if (row.original.clientStatus === "inactive") {
			menuItems.push({
				text: "Activate Client",
				disabled:
					row.original.clientLifecycleStatus ===
					TRASH_LIFECYCLE_STATUS,
				disabledHoverText: "Cannot activate a client in the Trash Bin",
				onSelect: () =>
					this.props.activateClient({
						clientID: row.original.clientID,
						location: "clientList",
					}),
			});
		}
		if (this.props.userRights.CAN_PERMANENT_DELETE) {
			if (row.original.clientLifecycleStatus === TRASH_LIFECYCLE_STATUS) {
				menuItems.push({
					text: "Restore Client from Trash Bin",
					onSelect: () =>
						this.props.setClientLifecycleStatus({
							clientIDs: [row.original.clientID],
							lifecycleStatus: NORMAL_LIFECYCLE_STATUS,
							location: "clientList",
						}),
				});
			}
			if (
				row.original.clientLifecycleStatus === NORMAL_LIFECYCLE_STATUS
			) {
				menuItems.push({
					text: "Move Client to Trash Bin",
					onSelect: () =>
						this.props.setClientLifecycleStatus({
							clientIDs: [row.original.clientID],
							lifecycleStatus: TRASH_LIFECYCLE_STATUS,
							location: "clientList",
						}),
				});
			}
		}
		return menuItems;
	};

	render() {
		return (
			<div className="fullHeightFlex noDefaultMargin">
				<CustomizableTable
					actionButtons={({ openCsvExport }) => (
						<>
							<ButtonWithRouter
								redirect="/clients/new"
								buttonText="New Client"
								iconComponent={
									<IconUsersGroup
										size={16}
										style={{ verticalAlign: "middle" }}
									/>
								}
								disabled={!this.props.canCreateClient}
								disabledHoverText="You do not have permissions to create new clients"
							/>
							<DropDownButtonWithRouter
								title="Actions"
								iconComponent={<LexWorkplaceGavelIcon />}
								pullRight
								menuItems={[
									{
										type: "label",
										text: (
											<>
												Trash toggle moved to Show{" "}
												<IconArrowUp
													size={14}
													style={{
														verticalAlign: "sub",
													}}
												/>
											</>
										),
									},
									{ divider: true },
									{
										text: "Export Data as CSV",
										onSelect: openCsvExport,
									},
								]}
							/>
						</>
					)}
					tabsFilterMap={this.filterMap}
					defaultTabFilterValue="Active"
					showTrash={this.props.showTrashClients}
					onToggleShowTrash={this.props.toggleShowTrashClients}
					className="fullWidth"
					loading={this.props.loading}
					data={this.props.clientListData.filter(
						(row) =>
							this.props.showTrashClients ||
							row.clientLifecycleStatus !== TRASH_LIFECYCLE_STATUS
					)}
					columns={this.columnConfig}
					autoResize
					noResultsText={"No Clients in this view"}
					tableUniqueID={tableUniqueID}
					csvExport={{
						extraColumns: [
							{
								Header: "In Trash",
								accessor: (row) =>
									row.clientLifecycleStatus ===
									TRASH_LIFECYCLE_STATUS
										? "Yes"
										: "No",
							},
						],
						csvColumnRenderers: {
							tags: (val) =>
								(val || []).map((t) => t.tagName).join("; "),
							primaryAttorneys: (val) =>
								(val || [])
									.map((s) => s.displayName)
									.join("; "),
						},
						fileName: "clients-export",
					}}
				/>
			</div>
		);
	}
}
ClientList.propTypes = {
	fetchClientList: PropTypes.func.isRequired,
	clientListData: PropTypes.array.isRequired,
};
const mapStateToProps = (state) => ({
	currentPathname: state.globalSearchbar.location,
	clientListData: state.client.clientListData,
	myUserID: state.user.userID,
	loading: state.request.loading,
	cognitoSub: state.user.cognitoUser.attributes.sub,
	canCreateClient: state.user.userRights.CREATE_CLIENT_ALLOWED,
	userRights: state.user.userRights,
	showTrashClients: state.browserLocalstorage.showTrashClients,
});
export default connect(mapStateToProps, {
	fetchClientList,
	setClientLifecycleStatus,
	deactivateClient,
	activateClient,
	toggleShowTrashClients,
})(ClientList);
